// --- Imports ---
import {
  Stack,
  Typography,
  Box,
  useTheme,
  Container,
  Grid,
  alpha,
} from "@mui/material";
import {
  Code,
  Storage,
  Animation,
  Terminal,
  Palette,
  RocketLaunch,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import SkillsCard from "./SkillsCard";

// --- Section Animation ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

// --- Main Section Component ---
export default function SkillsSection() {
  const theme = useTheme();

  const skills = [
    {
      title: "Frontend Development",
      icon: <Code fontSize="large" />,
      color: theme?.palette?.primary?.main,
      items: [
        "JavaScript (ES6+)",
        "React.js",
        "Next.js",
        "Formik & Yup",
        "TypeScript",
        "React Query",
      ],
      level: 75,
    },
    {
      title: "State Management",
      icon: <Storage fontSize="large" />,
      color: theme?.palette?.secondary?.main,
      items: ["Redux Toolkit", "Zustand", "Context API"],
      level: 65,
    },
    {
      title: "UI Animation",
      icon: <Animation fontSize="large" />,
      color: theme?.palette?.error?.main,
      items: ["Framer Motion", "GSAP"],
      level: 50,
    },
    {
      title: "Backend & APIs",
      icon: <Terminal fontSize="large" />,
      color: theme?.palette?.success?.main,
      items: ["Node.js", "Express.js", "RESTful APIs"],
      level: 40,
    },
    {
      title: "UI Styling",
      icon: <Palette fontSize="large" />,
      color: theme?.palette?.info?.main,
      items: ["Material UI", "Tailwind CSS", "Bootstrap", "SASS"],
      level: 80,
    },
    {
      title: "Dev Tools",
      icon: <RocketLaunch fontSize="large" />,
      color: theme?.palette?.warning?.main,
      items: ["Git & GitHub", "Vite", "Webpack", "NPM/Yarn"],
      level: 60,
    },
  ];

  return (
    <Box
      id="skills-section"
      sx={{
        py: { xs: 8, md: 12 },
        // minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        // backgroundColor: "#050505",
      }}
    >
      {/* --- Ambient Glowing Background --- */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 10% 20%, ${alpha(theme?.palette?.primary?.dark, 0.2)} 0%, transparent 40%),
            radial-gradient(circle at 90% 80%, ${alpha(theme?.palette?.secondary?.dark, 0.15)} 0%, transparent 40%),
            radial-gradient(circle at 50% 50%, ${alpha(theme?.palette?.info?.dark, 0.1)} 0%, transparent 60%)
          `,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* --- Cyber Grid Overlay --- */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(theme?.palette?.divider, 0.05)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme?.palette?.divider, 0.05)} 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
          maskImage:
            "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xxl" sx={{ position: "relative", zIndex: 10 }}>
        {/* --- Header --- */}
        <Box textAlign="center" mb={{ xs: 8, md: 10 }}>
          <Typography
            component={motion.h2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            variant="h4"
            fontWeight={800}
            mb={2}
            sx={{
              background: `linear-gradient(135deg, ${theme?.palette?.common?.white} 0%, ${theme?.palette?.primary?.light} 50%, ${theme?.palette?.secondary?.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: `drop-shadow(0 0 30px ${alpha(theme?.palette?.info?.main, 0.3)})`,
              letterSpacing: "1px",
            }}
          >
            Technical Skills
          </Typography>
          <Typography
            component={motion.p}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            sx={{
              color: alpha(theme?.palette?.text?.primary, 0.6),
              maxWidth: 600,
              mx: "auto",
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Technologies and tools I orchestrate daily to build modern,
            high-performance web applications.
          </Typography>
        </Box>

        {/* --- Skills Grid --- */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <Grid container spacing={1}>
            {skills?.map((skill) => (
              <SkillsCard key={skill?.title} skill={skill} />
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}
